import {ItemPicker, type ExtendedItemProps} from '@github-ui/item-picker-base/ItemPicker'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {useDebounce} from '@github-ui/use-debounce'
import React, {useCallback, useMemo, useState} from 'react'

import {useAlertRepositoriesQuery, type AlertRepository} from '../hooks/use-alert-repositories-query'
import {AlertPicker, type BaseAlert} from './AlertPicker'
import type {AlertsQueryRequest} from '../hooks/use-alerts-query'

// Picker type for the two-stage flow
export type AlertPickerType = 'repository' | 'alert' | null

// Props for the top-level RepositoryAndAlertPicker
export interface RepositoryAndAlertPickerProps<Alert> {
  anchorElement: (props: React.HTMLAttributes<HTMLElement>) => React.ReactElement

  onAlertSelection: (alerts: Alert[]) => void
  initialAlertSelection: Alert[]
  initialOwner: string
  initialRepo: string

  getAlertsQueryRequest: (currentOwner: string, currentRepo: string, filter: string) => AlertsQueryRequest
  getAlertRepositoriesPath: (owner: string) => string

  getAlertKey: (alert: Alert) => string
  getLeadingVisual: (alert: Alert) => React.ReactElement
}

export function RepositoryAndAlertPicker<Alert extends BaseAlert>({
  anchorElement,
  onAlertSelection,
  initialAlertSelection,
  initialOwner,
  initialRepo,
  getAlertsQueryRequest,
  getAlertRepositoriesPath,
  getAlertKey,
  getLeadingVisual,
}: RepositoryAndAlertPickerProps<Alert>) {
  const [pickerType, setPickerType] = useState<AlertPickerType>('alert')

  const [currentOwner, setCurrentOwner] = useState<string>(initialOwner)
  const [currentRepo, setCurrentRepo] = useState<string>(initialRepo)

  const handleBackClick = useCallback(() => {
    setPickerType('repository')
  }, [setPickerType])

  const handleRepositorySelected = useCallback(
    (owner: string, repo: string) => {
      setCurrentOwner(owner)
      setCurrentRepo(repo)
      setPickerType('alert')
    },
    [setCurrentOwner, setCurrentRepo, setPickerType],
  )

  const handleAlertSelection = useCallback(
    (alerts: Alert[]) => {
      onAlertSelection(alerts)
      setPickerType(null)
    },
    [onAlertSelection, setPickerType],
  )

  switch (pickerType) {
    case 'repository':
      return (
        <AlertRepositoryPicker
          anchorElement={anchorElement}
          onSelect={handleRepositorySelected}
          currentOwner={currentOwner}
          currentRepo={currentRepo}
          getAlertRepositoriesPath={getAlertRepositoriesPath}
        />
      )
    case 'alert':
      return (
        <AlertPicker<Alert>
          anchorElement={anchorElement}
          onSelectionChange={handleAlertSelection}
          onBackClick={handleBackClick}
          currentOwner={currentOwner}
          currentRepo={currentRepo}
          initialAlertSelection={initialAlertSelection}
          getAlertsQueryRequest={getAlertsQueryRequest}
          getAlertKey={getAlertKey}
          getLeadingVisual={getLeadingVisual}
        />
      )
  }
}

// Repository picker that fetches repos with security alerts
function AlertRepositoryPicker({
  anchorElement,
  onSelect,
  currentOwner,
  currentRepo,
  getAlertRepositoriesPath,
}: {
  anchorElement: (props: React.HTMLAttributes<HTMLElement>) => React.ReactElement
  onSelect: (owner: string, repo: string) => void
  currentOwner: string
  currentRepo: string
  getAlertRepositoriesPath: (org: string) => string
}) {
  const [filter, setFilter] = useState('')
  const [debouncedFilter, setDebouncedFilter] = useState('')
  const updateDebouncedFilter = useDebounce(setDebouncedFilter, VALUES.pickerDebounceTime)

  const handleFilterChange = useCallback(
    (value: string) => {
      setFilter(value)
      updateDebouncedFilter(value)
    },
    [updateDebouncedFilter],
  )

  const {org, query} = parseRepoSearchQuery(debouncedFilter, currentOwner)
  const {data, isPending} = useAlertRepositoriesQuery({
    path: getAlertRepositoriesPath(org),
    query,
  })

  const repositories = useMemo(() => data ?? [], [data])

  const initialSelected = useMemo(() => {
    if (org !== currentOwner) {
      return []
    }
    return repositories.filter(r => r.value === currentRepo)
  }, [repositories, org, currentOwner, currentRepo])

  const handleSelectionChange = useCallback(
    (selectedItems: AlertRepository[]) => {
      const repo = selectedItems[0]
      if (repo) {
        onSelect(org, repo.value)
      }
    },
    [onSelect, org],
  )

  return (
    <ItemPicker<AlertRepository>
      items={repositories}
      initialSelectedItems={initialSelected}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      selectionVariant="single"
      filterItems={handleFilterChange}
      initialFilter={filter}
      onSelectionChange={handleSelectionChange}
      loading={isPending}
      placeholderText="Search repositories..."
      title="Select a repository"
      width="medium"
      height="large"
      triggerOpen
      renderAnchor={anchorElement}
    />
  )
}

function getItemKey(repo: AlertRepository): string {
  return repo.value
}

function convertToItemProps(repo: AlertRepository): ExtendedItemProps<AlertRepository> {
  return {
    id: getItemKey(repo),
    text: repo.value,
    description: '',
    source: repo,
  }
}

function parseRepoSearchQuery(filter: string, currentOwner: string): {org: string; query: string} {
  const i = filter.indexOf('/')
  if (i === -1) {
    return {org: currentOwner, query: filter}
  } else {
    return {org: filter.slice(0, i), query: filter.slice(i + 1)}
  }
}
